import { Heart, Shield, Sparkles, Home } from "lucide-react";

const features = [
  {
    icon: Heart,
    title: "Affectueux",
    description: "Un tempérament doux et calme, idéal pour les familles avec enfants.",
  },
  {
    icon: Shield,
    title: "Robuste",
    description: "Une race naturelle et solide, peu sujette aux maladies génétiques.",
  },
  {
    icon: Sparkles,
    title: "Hypoallergénique",
    description: "Produit moins de protéine Fel d1, souvent toléré par les allergiques.",
  },
  {
    icon: Home,
    title: "Adaptable",
    description: "S'intègre parfaitement à tout type de foyer et mode de vie.",
  },
];

export default function BreedSection() {
  return (
    <section
      id="race"
      data-testid="breed-section"
      className="relative py-24 lg:py-32 bg-cream-alt overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sand/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-forest/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-16">
          <span className="font-accent text-autumn text-xl">Une race d'exception</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Le Chat Sibérien
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-3xl mx-auto">
            Originaire des forêts de Sibérie, le chat Sibérien est une race naturelle
            qui a su conquérir les cœurs du monde entier par sa beauté majestueuse
            et son caractère exceptionnel.
          </p>
        </div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className="section-animate order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-sand rounded-3xl" />
              <img
                src="https://customer-assets.emergentagent.com/job_siberian-haven/artifacts/cdepsbqu_IMG-20240225-WA0011.jpg"
                alt="Chat Sibérien majestueux"
                className="relative rounded-3xl shadow-hover w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-soft">
                <p className="font-accent text-forest text-lg">Race naturelle</p>
                <p className="font-body text-sm text-forest/70">depuis plus de 1000 ans</p>
              </div>
            </div>
          </div>

          {/* Text content */}
          <div className="order-1 lg:order-2 space-y-8">
            <div className="section-animate">
              <h3 className="font-heading text-2xl font-semibold text-forest mb-4">
                Origines et Histoire
              </h3>
              <p className="font-body text-forest/80 leading-relaxed">
                Le Sibérien est une race ancestrale originaire des forêts de Russie.
                Adapté aux rudes conditions climatiques, il possède un pelage dense
                et imperméable qui le protège du froid. Cette race naturelle n'a pas
                été créée par l'homme mais s'est développée au fil des siècles dans
                son environnement naturel.
              </p>
            </div>

            <div className="section-animate">
              <h3 className="font-heading text-2xl font-semibold text-forest mb-4">
                Un Compagnon Idéal
              </h3>
              <p className="font-body text-forest/80 leading-relaxed">
                Intelligent, joueur et incroyablement affectueux, le Sibérien est le
                compagnon parfait pour toute la famille. Il s'entend merveilleusement
                avec les enfants et les autres animaux. Son pelage, souvent qualifié
                d'hypoallergénique, en fait un excellent choix pour les personnes
                sensibles aux allergies félines.
              </p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="section-animate feature-card bg-white/80 backdrop-blur-sm border border-sand/50 rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-forest" />
              </div>
              <h4 className="font-heading text-lg font-semibold text-forest mb-2">
                {feature.title}
              </h4>
              <p className="font-body text-sm text-forest/70">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
